import { useState } from "react";
import API from "../lib/api";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Button } from "./ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { UploadSimple, Trash, CheckCircle, Spinner, FileArrowUp } from "@phosphor-icons/react";
import { toast } from "sonner";

import DateInput from "./DateInput";
export const DOC_TYPES = [
  "insurance", "public_liability", "employers_liability", "professional_indemnity",
  "gas_safe", "niceic", "napit", "chas", "safe_contractor", "constructionline",
  "dbs_check", "cscs_card", "asbestos_awareness", "first_aid", "fire_safety",
  "method_statement", "risk_assessment", "waste_carrier_license", "contract", "accreditation", "other",
];

const prettify = (t) => t.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

// Public multi-document uploader for the /apply form. Uploads each file to the
// public endpoint immediately, then keeps a tagged list (type + expiry) in state.
export const ApplicationDocumentUpload = ({ value = [], onChange, formToken = "", docTypes }) => {
  const TYPES = docTypes && docTypes.length ? docTypes : DOC_TYPES;
  const [uploading, setUploading] = useState(false);
  const [docType, setDocType] = useState("public_liability");
  const [expiry, setExpiry] = useState("");

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (value.length >= 15) { toast.error("You can attach up to 15 documents"); return; }
    if (file.size > 20 * 1024 * 1024) { toast.error("File too large (max 20MB)"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("form_token", formToken);
      const { data } = await API.post("/public/application-upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onChange([...value, {
        file_id: data.file_id, file_name: data.file_name,
        doc_type: docType, expiry_date: expiry,
      }]);
      setExpiry("");
      toast.success(`${data.file_name} attached`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed — please try again");
    } finally {
      setUploading(false);
    }
  };

  const removeAt = (i) => onChange(value.filter((_, idx) => idx !== i));
  const updateAt = (i, patch) => onChange(value.map((d, idx) => (idx === i ? { ...d, ...patch } : d)));

  return (
    <div data-testid="application-documents">
      <div className="grid gap-2 sm:grid-cols-[1fr_auto_auto] items-end border border-border rounded-lg p-3 bg-muted/20">
        <div>
          <Label className="text-[10px]">Document type</Label>
          <Select value={docType} onValueChange={setDocType}>
            <SelectTrigger className="h-9 text-xs" data-testid="app-doc-type"><SelectValue /></SelectTrigger>
            <SelectContent className="max-h-64">
              {TYPES.map((t) => <SelectItem key={t} value={t}>{prettify(t)}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-[10px]">Expiry date <span className="text-muted-foreground">(optional)</span></Label>
          <DateInput value={expiry} onChange={(e) => setExpiry(e.target.value)} className="h-9 text-xs" data-testid="app-doc-expiry" />
        </div>
        <label className="cursor-pointer">
          <input type="file" className="hidden" onChange={handleFile} accept="image/*,application/pdf,.doc,.docx,.xls,.xlsx" data-testid="app-doc-file-input" disabled={uploading} />
          <span className={`inline-flex items-center justify-center gap-1.5 h-9 px-4 rounded-md text-xs font-medium border ${uploading ? "opacity-60" : "hover:bg-muted"} border-input`} data-testid="app-doc-upload-btn">
            {uploading ? <Spinner size={14} className="animate-spin" /> : <UploadSimple size={14} />}
            {uploading ? "Uploading…" : "Upload"}
          </span>
        </label>
      </div>
      <p className="text-[10px] text-muted-foreground mt-1.5">Pick a type & expiry, then upload. PDF, images or Office docs, up to 20MB each (max 15).</p>

      {value.length > 0 ? (
        <div className="mt-3 space-y-2" data-testid="app-doc-list">
          {value.map((d, i) => (
            <div key={d.file_id} className="flex flex-wrap items-center gap-2 border border-border rounded-lg p-2.5" data-testid={`app-doc-row-${i}`}>
              <CheckCircle size={16} weight="fill" className="text-emerald-500 shrink-0" />
              <span className="text-xs font-medium truncate max-w-[160px]" title={d.file_name}>{d.file_name}</span>
              <Select value={d.doc_type} onValueChange={(v) => updateAt(i, { doc_type: v })}>
                <SelectTrigger className="h-7 text-[11px] w-40" data-testid={`app-doc-row-type-${i}`}><SelectValue /></SelectTrigger>
                <SelectContent className="max-h-64">
                  {TYPES.map((t) => <SelectItem key={t} value={t}>{prettify(t)}</SelectItem>)}
                </SelectContent>
              </Select>
              <div className="flex items-center gap-1">
                <span className="text-[10px] text-muted-foreground">Expires</span>
                <DateInput value={d.expiry_date || ""} onChange={(e) => updateAt(i, { expiry_date: e.target.value })} className="h-7 text-[11px] w-36" data-testid={`app-doc-row-expiry-${i}`} />
              </div>
              <Button type="button" variant="ghost" size="sm" className="h-7 w-7 p-0 ml-auto text-red-500" onClick={() => removeAt(i)} data-testid={`app-doc-remove-${i}`}>
                <Trash size={14} />
              </Button>
            </div>
          ))}
        </div>
      ) : (
        <div className="mt-3 flex items-center gap-2 text-xs text-muted-foreground border border-dashed border-border rounded-lg p-4 justify-center" data-testid="app-doc-empty">
          <FileArrowUp size={16} /> No documents attached yet
        </div>
      )}
    </div>
  );
};
